import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, ProtectedRoute } from "@/lib/auth";
import { ChildProvider } from "@/lib/childContext";
import { I18nProvider } from "@/lib/i18n";

import Landing from "@/pages/Landing";
import SignIn from "@/pages/SignIn";
import SignUp from "@/pages/SignUp";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Home from "@/pages/Home";
import Moments from "@/pages/Moments";
import Messages from "@/pages/Messages";
import ChildProfile from "@/pages/ChildProfile";
import Trust from "@/pages/Trust";
import Caregiver from "@/pages/Caregiver";
import Settings from "@/pages/Settings";
import Workspace from "@/pages/Workspace";
import Accept from "@/pages/Accept";
import ChildMode from "@/pages/ChildMode";
import Pickup from "@/pages/Pickup";
import Calendar from "@/pages/Calendar";
import More from "@/pages/More";
import Documents from "@/pages/Documents";
import Notifications from "@/pages/Notifications";
import Staff from "@/pages/Staff";
import Roster from "@/pages/Roster";
import RosterLog from "@/pages/RosterLog";
import HealthLog from "@/pages/HealthLog";
import Gallery from "@/pages/Gallery";
import Pricing from "@/pages/Pricing";
import Demo from "@/pages/Demo";
import About from "@/pages/About";
import { Privacy, Terms, Security, Coppa, Gdpr } from "@/pages/legal/LegalPages";
import AdminLayout from "@/pages/AdminLayout";
import AdminOverview from "@/pages/admin/Overview";
import AdminChildren from "@/pages/admin/AdminChildren";
import { AdminParents, AdminStaff as AdminStaffPage } from "@/pages/admin/AdminUsers";
import AdminGroups from "@/pages/admin/AdminGroups";
import AdminReports from "@/pages/admin/AdminReports";
import AdminAuditLogs from "@/pages/admin/AdminAuditLogs";
import AdminDemoRequests from "@/pages/admin/AdminDemoRequests";
import AdminCalendar from "@/pages/admin/AdminCalendar";
import AdminPickupLog from "@/pages/admin/AdminPickupLog";
import AdminPrivacy from "@/pages/admin/AdminPrivacy";
import AdminTranslationFeedback from "@/pages/admin/AdminTranslationFeedback";
import AdminI18nCoverage from "@/pages/admin/AdminI18nCoverage";
import AdminVendors from "@/pages/admin/AdminVendors";
import AdminDocumentsHub from "@/pages/admin/AdminDocumentsHub";
import AdminAccounting from "@/pages/admin/AdminAccounting";
import AdminCalendarSync from "@/pages/admin/AdminCalendarSync";
import AdmissionsPipeline from "@/pages/admin/AdmissionsPipeline";
import AdmissionsLeadDetail from "@/pages/admin/AdmissionsLeadDetail";
import AdmissionsDocuments from "@/pages/admin/AdmissionsDocuments";
import AdmissionsInquiry from "@/pages/AdmissionsInquiry";
import AdmissionsApply from "@/pages/AdmissionsApply";
import ParentApplication from "@/pages/ParentApplication";
import AdminSmartBuilding from "@/pages/admin/AdminSmartBuilding";
import AdminStatusHistory from "@/pages/admin/AdminStatusHistory";
import PanelHub from "@/pages/PanelHub";
import SignageHub from "@/pages/SignageHub";
import AdminPermissions from "@/pages/admin/AdminPermissions";
import BillingDashboard from "@/pages/admin/BillingDashboard";
import BillingInvoices from "@/pages/admin/BillingInvoices";
import BillingInvoiceDetail from "@/pages/admin/BillingInvoiceDetail";
import BillingFeePlans from "@/pages/admin/BillingFeePlans";
import BillingSettings from "@/pages/admin/BillingSettings";
import ParentBilling from "@/pages/Billing";
import BillingInvoicePrint from "@/pages/BillingInvoicePrint";
import AdminBilling from "@/pages/admin/AdminBilling";
import RatioDashboard from "@/pages/admin/RatioDashboard";
import DailyLogs from "@/pages/admin/DailyLogs";
import IncidentReports from "@/pages/admin/IncidentReports";
import IncidentDetail from "@/pages/admin/IncidentDetail";
import IncidentCreate from "@/pages/admin/IncidentCreate";
import MealPlans from "@/pages/admin/MealPlans";
import StaffSchedule from "@/pages/admin/StaffSchedule";
import Certifications from "@/pages/admin/Certifications";
import Observations from "@/pages/admin/Observations";
import ParentDailyStory from "@/pages/ParentDailyStory";
import IncidentAck from "@/pages/IncidentAck";
import BottomNav from "@/components/BottomNav";
import DemoToolbar from "@/components/DemoToolbar";

function App() {
  return (
    <div className="App grain pb-24 sm:pb-28">
      <BrowserRouter>
        <I18nProvider>
          <AuthProvider>
            <ChildProvider>
            <Toaster
              position="top-center"
              toastOptions={{
                style: {
                  background: "#FFFFFF",
                  color: "#2C2B29",
                  border: "1px solid rgba(44,43,41,0.08)",
                  fontFamily: "'Outfit', sans-serif",
                  fontWeight: 300,
                  borderRadius: "9999px",
                  padding: "12px 20px",
                },
              }}
            />
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/demo" element={<Demo />} />
              <Route path="/about" element={<About />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/security" element={<Security />} />
              <Route path="/coppa" element={<Coppa />} />
              <Route path="/gdpr" element={<Gdpr />} />
              <Route path="/signin" element={<SignIn />} />
              <Route path="/signup" element={<SignUp />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/accept/:kind/:token" element={<Accept />} />
              <Route path="/admissions/inquire" element={<AdmissionsInquiry />} />
              <Route path="/admissions/apply/:token" element={<AdmissionsApply />} />
              <Route
                path="/billing"
                element={
                  <ProtectedRoute>
                    <ParentBilling />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/billing/invoices/:id/print"
                element={
                  <ProtectedRoute>
                    <BillingInvoicePrint />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/today-story/:childId"
                element={
                  <ProtectedRoute>
                    <ParentDailyStory />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/incidents/:id"
                element={
                  <ProtectedRoute>
                    <IncidentAck />
                  </ProtectedRoute>
                }
              />
              <Route path="/panel" element={<PanelHub />} />
              <Route path="/signage" element={<SignageHub />} />
              <Route
                path="/with/:childId"
                element={
                  <ProtectedRoute>
                    <ChildMode />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/home"
                element={
                  <ProtectedRoute>
                    <Home />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/moments"
                element={
                  <ProtectedRoute>
                    <Moments />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/messages"
                element={
                  <ProtectedRoute>
                    <Messages />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/child"
                element={
                  <ProtectedRoute>
                    <ChildProfile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/trust"
                element={
                  <ProtectedRoute>
                    <Trust />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/caregiver"
                element={
                  <ProtectedRoute>
                    <Caregiver />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/workspace"
                element={
                  <ProtectedRoute>
                    <Workspace />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/pickup"
                element={
                  <ProtectedRoute>
                    <Pickup />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/calendar"
                element={
                  <ProtectedRoute>
                    <Calendar />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/more"
                element={
                  <ProtectedRoute>
                    <More />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/more/documents"
                element={
                  <ProtectedRoute>
                    <Documents />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/more/health"
                element={
                  <ProtectedRoute>
                    <HealthLog />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/more/gallery"
                element={
                  <ProtectedRoute>
                    <Gallery />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/notifications"
                element={
                  <ProtectedRoute>
                    <Notifications />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/staff"
                element={
                  <ProtectedRoute>
                    <Staff />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/roster"
                element={
                  <ProtectedRoute>
                    <Roster />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/roster/log"
                element={
                  <ProtectedRoute>
                    <RosterLog />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<AdminOverview />} />
                <Route path="children" element={<AdminChildren />} />
                <Route path="parents" element={<AdminParents />} />
                <Route path="staff" element={<AdminStaffPage />} />
                <Route path="groups" element={<AdminGroups />} />
                <Route path="reports" element={<AdminReports />} />
                <Route path="audit-logs" element={<AdminAuditLogs />} />
                <Route path="demo-requests" element={<AdminDemoRequests />} />
                <Route path="status-history" element={<AdminStatusHistory />} />
                <Route path="pickup-log" element={<AdminPickupLog />} />
                <Route path="privacy" element={<AdminPrivacy />} />
                <Route path="translation-feedback" element={<AdminTranslationFeedback />} />
                <Route path="i18n-coverage" element={<AdminI18nCoverage />} />
                <Route path="vendors" element={<AdminVendors />} />
                <Route path="documents-hub" element={<AdminDocumentsHub />} />
                <Route path="accounting" element={<AdminAccounting />} />
                <Route path="calendar-sync" element={<AdminCalendarSync />} />
                <Route path="admissions" element={<AdmissionsPipeline />} />
                <Route path="admissions/leads/:id" element={<AdmissionsLeadDetail />} />
                <Route path="admissions/documents" element={<AdmissionsDocuments />} />
                <Route path="smart-building" element={<AdminSmartBuilding />} />
                <Route path="permissions" element={<AdminPermissions />} />
                <Route path="billing" element={<BillingDashboard />} />
                <Route path="billing/invoices" element={<BillingInvoices />} />
                <Route path="billing/invoices/:id" element={<BillingInvoiceDetail />} />
                <Route path="billing/fee-plans" element={<BillingFeePlans />} />
                <Route path="billing/settings" element={<BillingSettings />} />
                <Route path="billing-legacy" element={<AdminBilling />} />
                <Route path="ops/ratio" element={<RatioDashboard />} />
                <Route path="ops/daily-logs" element={<DailyLogs />} />
                <Route path="ops/observations" element={<Observations />} />
                <Route path="ops/incidents" element={<IncidentReports />} />
                <Route path="ops/incidents/new" element={<IncidentCreate />} />
                <Route path="ops/incidents/:id" element={<IncidentDetail />} />
                <Route path="ops/meal-plans" element={<MealPlans />} />
                <Route path="ops/schedule" element={<StaffSchedule />} />
                <Route path="ops/certifications" element={<Certifications />} />
                <Route path="attendance" element={<Roster />} />
                <Route path="calendar" element={<AdminCalendar />} />
                <Route path="messages" element={<Staff />} />
                <Route path="documents" element={<Documents />} />
                <Route path="settings" element={<More />} />
              </Route>
            </Routes>
            <BottomNav />
            <DemoToolbar />
          </ChildProvider>
        </AuthProvider>
        </I18nProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
